import React, { useState } from 'react';
import PropTypes from 'prop-types';

import {
  appointments as appointmentsDuck,
  calendars as calendarsDuck,
  schedules as schedulesDuck,
  modals
} from 'ducks';

import { duckify } from 'utils/duckify';

import ModalRoot from 'components/ModalRoot';
import PageHeader from 'components/PageHeader';
import AppointmentCancelConfirm from './AppointmentCancelConfirm';
import AppointmentList from './AppointmentList';
import Sidebar from './Sidebar';

import './styles/appointment_list.scss';

const AppointmentListPage = ({ appointments, calendars, schedules, handleAppointmentCancel }) => {
  const [deselectedSchedules, setDeselectedSchedules] = useState([]);

  if (schedules.length === 0) {
    return (
      <React.Fragment>
        <PageHeader header='Appointments' />
        {
          schedules.ready && (
            <div>
              There are no calendars configured. Visit the&nbsp;
              <a href='/admin/calendars'>calendars</a>
              &nbsp;page to configure one.
            </div>
          )
        }
      </React.Fragment>
    );
  }

  const toggleScheduleSelection = (schedule) => {
    if (deselectedSchedules.includes(schedule.id)) {
      return deselectedSchedules.filter((scheduleId) => scheduleId !== schedule.id);
    }

    return deselectedSchedules.concat([schedule.id]);
  };

  const toggleCalendarSelection = (calendar) => {
    const subSchedules = schedules.filter((schedule) => schedule.calendar_id === calendar.id);

    // If any of this calendar's schedules are already deselected
    if (subSchedules.some((schedule) => deselectedSchedules.includes(schedule.id))) {
      return deselectedSchedules.filter((schedule) => subSchedules.includes(schedule.id));
    }

    return deselectedSchedules.concat(subSchedules.map((schedule) => schedule.id));
  };

  const selectedAppointments = appointments
    .filter((appointment) => !deselectedSchedules.includes(appointment.schedule_id));

  return (
    <React.Fragment>
      <ModalRoot modals={{ AppointmentCancelConfirm }} />
      <PageHeader header='Appointments' />
      <div className='appointment-list-wrapper'>
        <Sidebar
          calendars={calendars}
          deselectedSchedules={deselectedSchedules}
          schedules={schedules}
          onCalendarToggle={(calendar) => setDeselectedSchedules(toggleCalendarSelection(calendar))}
          onScheduleToggle={(schedule) => setDeselectedSchedules(toggleScheduleSelection(schedule))}
        />
        <AppointmentList appointments={selectedAppointments} onAppointmentCancel={handleAppointmentCancel} />
      </div>
    </React.Fragment>
  );
};

AppointmentListPage.propTypes = {
  /** A list of appointments */
  appointments: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })),

  /** A list of calendars */
  calendars: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })),

  /** Called when the "Cancel Appointment" button is clicked next to a single appointment */
  handleAppointmentCancel: PropTypes.func,

  /** A list of schedules */
  schedules: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string }))
};

AppointmentListPage.defaultProps = {
  appointments: [],
  calendars: [],
  schedules: [],

  handleAppointmentCancel: () => false
};

const mapDucksToProps = () => ({
  appointments: appointmentsDuck,
  calendars: calendarsDuck,
  schedules: schedulesDuck
});

const mapDispatchToProps = (dispatch) => ({
  handleAppointmentCancel: (appointment) => {
    dispatch(modals.actions.show('AppointmentCancelConfirm', {
      ...appointment,
      onCancel: (id) => dispatch(appointmentsDuck.actions.remove({ id })).then(() => {
        dispatch(appointmentsDuck.actions.markDirty());
        dispatch(modals.actions.hide('AppointmentCancelConfirm'));
      })
    }));
  }
});

export default duckify(mapDucksToProps, () => ({}), mapDispatchToProps)(AppointmentListPage);
